import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { clientAPI, projectAPI, videoAPI, rateAPI } from '@/utils/api';
import { formatDate, formatCurrency } from '@/utils/formatters';
import { FolderKanban, Plus, Building2, Briefcase, Video, DollarSign } from 'lucide-react';
import { toast } from 'sonner';

const AdminProjects = () => {
  const { user } = useOutletContext();
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [videos, setVideos] = useState([]);
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showClientDialog, setShowClientDialog] = useState(false);
  const [showProjectDialog, setShowProjectDialog] = useState(false);
  const [showVideoDialog, setShowVideoDialog] = useState(false);
  const [showRateDialog, setShowRateDialog] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  
  const [clientForm, setClientForm] = useState({ name: '', contact_person: '', email: '' });
  const [projectForm, setProjectForm] = useState({ client_id: '', name: '', description: '', default_rate: '' });
  const [videoForm, setVideoForm] = useState({ project_id: '', title: '', video_identifier: '', duration_minutes: '' });
  const [rateForm, setRateForm] = useState({ project_id: '', task_type: 'translation', rate_per_minute: '' });
  
  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = async () => {
    try {
      const [clientsRes, projectsRes] = await Promise.all([
        clientAPI.list(),
        projectAPI.list()
      ]);
      setClients(clientsRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };
  
  const loadVideos = async (projectId) => {
    try {
      const response = await videoAPI.list(projectId);
      setVideos(response.data);
    } catch (error) {
      console.error('Error loading videos:', error);
    }
  };
  
  const loadRates = async (projectId) => {
    try {
      const response = await rateAPI.list(projectId);
      setRates(response.data);
    } catch (error) {
      console.error('Error loading rates:', error);
    }
  };
  
  const handleCreateClient = async (e) => {
    e.preventDefault();
    try {
      await clientAPI.create(clientForm);
      toast.success('Client created successfully!');
      setShowClientDialog(false);
      setClientForm({ name: '', contact_person: '', email: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create client');
    }
  };
  
  const handleCreateProject = async (e) => {
    e.preventDefault();
    try {
      await projectAPI.create({
        ...projectForm,
        default_rate: projectForm.default_rate ? parseFloat(projectForm.default_rate) : null
      });
      toast.success('Project created successfully!');
      setShowProjectDialog(false);
      setProjectForm({ client_id: '', name: '', description: '', default_rate: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create project');
    }
  };
  
  const handleCreateVideo = async (e) => {
    e.preventDefault();
    try {
      await videoAPI.create({
        ...videoForm,
        duration_minutes: parseFloat(videoForm.duration_minutes)
      });
      toast.success('Video created successfully!');
      setShowVideoDialog(false);
      setVideoForm({ project_id: '', title: '', video_identifier: '', duration_minutes: '' });
      if (selectedProject) {
        loadVideos(selectedProject);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create video');
    }
  };
  
  const handleCreateRate = async (e) => {
    e.preventDefault();
    try {
      await rateAPI.set({
        ...rateForm,
        rate_per_minute: parseFloat(rateForm.rate_per_minute)
      });
      toast.success('Rate set successfully!');
      setShowRateDialog(false);
      setRateForm({ project_id: '', task_type: 'translation', rate_per_minute: '' });
      if (selectedProject) {
        loadRates(selectedProject);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set rate');
    }
  };
  
  const handleProjectSelect = (projectId) => {
    setSelectedProject(projectId);
    loadVideos(projectId);
    loadRates(projectId);
  };
  
  return (
    <DashboardLayout user={user}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-heading font-bold text-primary mb-2">Manage Projects</h1>
            <p className="text-muted-foreground">Create and manage clients, projects, and videos</p>
          </div>
        </div>
        
        <Tabs defaultValue="clients" className="space-y-4">
          <TabsList>
            <TabsTrigger value="clients" data-testid="tab-clients">Clients</TabsTrigger>
            <TabsTrigger value="projects" data-testid="tab-projects">Projects</TabsTrigger>
            <TabsTrigger value="videos" data-testid="tab-videos">Videos</TabsTrigger>
            <TabsTrigger value="rates" data-testid="tab-rates">Rates</TabsTrigger>
          </TabsList>
          
          {/* Clients Tab */}
          <TabsContent value="clients">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-accent" />
                    Clients ({clients.length})
                  </CardTitle>
                  <Dialog open={showClientDialog} onOpenChange={setShowClientDialog}>
                    <DialogTrigger asChild>
                      <Button data-testid="btn-add-client">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Client
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Add New Client</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleCreateClient} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="client_name">Client Name *</Label>
                          <Input
                            id="client_name"
                            value={clientForm.name}
                            onChange={(e) => setClientForm({...clientForm, name: e.target.value})}
                            required
                            data-testid="input-client-name"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="contact_person">Contact Person</Label>
                          <Input
                            id="contact_person"
                            value={clientForm.contact_person}
                            onChange={(e) => setClientForm({...clientForm, contact_person: e.target.value})}
                            data-testid="input-contact-person"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="client_email">Email</Label>
                          <Input
                            id="client_email"
                            type="email"
                            value={clientForm.email}
                            onChange={(e) => setClientForm({...clientForm, email: e.target.value})}
                            data-testid="input-client-email"
                          />
                        </div>
                        <Button type="submit" className="w-full" data-testid="btn-save-client">
                          Create Client
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {clients.length === 0 ? (
                  <div className="text-center py-12">
                    <Building2 className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No clients yet. Add your first client.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {clients.map((client) => (
                      <div key={client.client_id} className="p-4 rounded-lg border border-border hover:border-accent transition-colors" data-testid={`client-${client.client_id}`}>
                        <h4 className="font-semibold text-primary mb-1">{client.name}</h4>
                        {client.contact_person && (
                          <p className="text-sm text-muted-foreground">Contact: {client.contact_person}</p>
                        )}
                        {client.email && (
                          <p className="text-sm text-muted-foreground">Email: {client.email}</p>
                        )}
                        <p className="text-xs text-muted-foreground mt-2">Added: {formatDate(client.created_at)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Projects Tab */}
          <TabsContent value="projects">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Briefcase className="w-5 h-5 text-accent" />
                    Projects ({projects.length})
                  </CardTitle>
                  <Dialog open={showProjectDialog} onOpenChange={setShowProjectDialog}>
                    <DialogTrigger asChild>
                      <Button data-testid="btn-add-project">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Project
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Add New Project</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleCreateProject} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="project_client">Client *</Label>
                          <Select value={projectForm.client_id} onValueChange={(value) => setProjectForm({...projectForm, client_id: value})} required>
                            <SelectTrigger data-testid="select-project-client">
                              <SelectValue placeholder="Select client" />
                            </SelectTrigger>
                            <SelectContent>
                              {clients.map((client) => (
                                <SelectItem key={client.client_id} value={client.client_id}>
                                  {client.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="project_name">Project Name *</Label>
                          <Input
                            id="project_name"
                            value={projectForm.name}
                            onChange={(e) => setProjectForm({...projectForm, name: e.target.value})}
                            required
                            data-testid="input-project-name"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="project_description">Description</Label>
                          <Input
                            id="project_description"
                            value={projectForm.description}
                            onChange={(e) => setProjectForm({...projectForm, description: e.target.value})}
                            data-testid="input-project-description"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="default_rate">Default Rate (₹/min)</Label>
                          <Input
                            id="default_rate"
                            type="number"
                            step="0.01"
                            value={projectForm.default_rate}
                            onChange={(e) => setProjectForm({...projectForm, default_rate: e.target.value})}
                            data-testid="input-default-rate"
                          />
                        </div>
                        <Button type="submit" className="w-full" data-testid="btn-save-project">
                          Create Project
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {projects.length === 0 ? (
                  <div className="text-center py-12">
                    <Briefcase className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No projects yet. Create your first project.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {projects.map((project) => (
                      <div key={project.project_id} className="p-4 rounded-lg border border-border hover:border-accent transition-colors" data-testid={`project-${project.project_id}`}>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="font-semibold text-primary mb-1">{project.name}</h4>
                            <p className="text-sm text-muted-foreground mb-2">Client: {project.client_name}</p>
                            {project.description && (
                              <p className="text-sm text-muted-foreground mb-2">{project.description}</p>
                            )}
                            {project.default_rate && (
                              <p className="text-sm text-muted-foreground">Default Rate: {formatCurrency(project.default_rate)}/min</p>
                            )}
                            <p className="text-xs text-muted-foreground mt-2">Created: {formatDate(project.created_at)}</p>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleProjectSelect(project.project_id)}
                            data-testid={`btn-view-project-${project.project_id}`}
                          >
                            View Details
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Videos Tab */}
          <TabsContent value="videos">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Video className="w-5 h-5 text-accent" />
                    Videos {selectedProject && `(${videos.length})`}
                  </CardTitle>
                  <Dialog open={showVideoDialog} onOpenChange={setShowVideoDialog}>
                    <DialogTrigger asChild>
                      <Button data-testid="btn-add-video">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Video
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Add New Video</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleCreateVideo} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="video_project">Project *</Label>
                          <Select value={videoForm.project_id} onValueChange={(value) => setVideoForm({...videoForm, project_id: value})} required>
                            <SelectTrigger data-testid="select-video-project">
                              <SelectValue placeholder="Select project" />
                            </SelectTrigger>
                            <SelectContent>
                              {projects.map((project) => (
                                <SelectItem key={project.project_id} value={project.project_id}>
                                  {project.client_name} - {project.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="video_title">Video Title *</Label>
                          <Input
                            id="video_title"
                            value={videoForm.title}
                            onChange={(e) => setVideoForm({...videoForm, title: e.target.value})}
                            required
                            data-testid="input-video-title"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="video_identifier">Video ID/Identifier</Label>
                          <Input
                            id="video_identifier"
                            value={videoForm.video_identifier}
                            onChange={(e) => setVideoForm({...videoForm, video_identifier: e.target.value})}
                            data-testid="input-video-identifier"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="duration_minutes">Duration (minutes) *</Label>
                          <Input
                            id="duration_minutes"
                            type="number"
                            step="0.1"
                            value={videoForm.duration_minutes}
                            onChange={(e) => setVideoForm({...videoForm, duration_minutes: e.target.value})}
                            required
                            data-testid="input-duration-minutes"
                          />
                        </div>
                        <Button type="submit" className="w-full" data-testid="btn-save-video">
                          Create Video
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {!selectedProject ? (
                  <div className="text-center py-12">
                    <Video className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">Select a project from the Projects tab to view its videos</p>
                  </div>
                ) : videos.length === 0 ? (
                  <div className="text-center py-12">
                    <Video className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No videos in this project yet. Add your first video.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {videos.map((video) => (
                      <div key={video.video_id} className="p-4 rounded-lg border border-border hover:border-accent transition-colors" data-testid={`video-${video.video_id}`}>
                        <h4 className="font-semibold text-primary mb-1">{video.title}</h4>
                        {video.video_identifier && (
                          <p className="text-sm text-muted-foreground">ID: {video.video_identifier}</p>
                        )}
                        <p className="text-sm text-muted-foreground">Duration: {video.duration_minutes} minutes</p>
                        <p className="text-sm text-muted-foreground">Status: {video.status}</p>
                        <p className="text-xs text-muted-foreground mt-2">Added: {formatDate(video.created_at)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Rates Tab */}
          <TabsContent value="rates">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-accent" />
                    Task Rates {selectedProject && `(${rates.length})`}
                  </CardTitle>
                  <Dialog open={showRateDialog} onOpenChange={setShowRateDialog}>
                    <DialogTrigger asChild>
                      <Button data-testid="btn-add-rate">
                        <Plus className="w-4 h-4 mr-2" />
                        Set Rate
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Set Task Rate</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleCreateRate} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="rate_project">Project *</Label>
                          <Select value={rateForm.project_id} onValueChange={(value) => setRateForm({...rateForm, project_id: value})} required>
                            <SelectTrigger data-testid="select-rate-project">
                              <SelectValue placeholder="Select project" />
                            </SelectTrigger>
                            <SelectContent>
                              {projects.map((project) => (
                                <SelectItem key={project.project_id} value={project.project_id}>
                                  {project.client_name} - {project.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="task_type">Task Type *</Label>
                          <Select value={rateForm.task_type} onValueChange={(value) => setRateForm({...rateForm, task_type: value})} required>
                            <SelectTrigger data-testid="select-task-type">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="translation">Translation</SelectItem>
                              <SelectItem value="qc">QC (Quality Check)</SelectItem>
                              <SelectItem value="template">Template</SelectItem>
                              <SelectItem value="other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="rate_per_minute">Rate per Minute (₹) *</Label>
                          <Input
                            id="rate_per_minute"
                            type="number"
                            step="0.01"
                            value={rateForm.rate_per_minute}
                            onChange={(e) => setRateForm({...rateForm, rate_per_minute: e.target.value})}
                            required
                            data-testid="input-rate-per-minute"
                          />
                        </div>
                        <Button type="submit" className="w-full" data-testid="btn-save-rate">
                          Set Rate
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {!selectedProject ? (
                  <div className="text-center py-12">
                    <DollarSign className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">Select a project from the Projects tab to manage its rates</p>
                  </div>
                ) : rates.length === 0 ? (
                  <div className="text-center py-12">
                    <DollarSign className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No rates configured for this project yet. Set task rates.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {rates.map((rate) => (
                      <div key={rate.rate_id} className="p-4 rounded-lg border border-border hover:border-accent transition-colors" data-testid={`rate-${rate.rate_id}`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-semibold text-primary mb-1 capitalize">{rate.task_type}</h4>
                            <p className="text-sm text-muted-foreground">Rate: {formatCurrency(rate.rate_per_minute)}/minute</p>
                            <p className="text-xs text-muted-foreground mt-2">Set on: {formatDate(rate.created_at)}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default AdminProjects;
